import { Component, type ReactNode } from "react";

interface RenderErrorBoundaryProps {
  readonly children: ReactNode;
  /** A function fallback is given what was thrown, for a view that says what failed. */
  readonly fallback: ReactNode | ((error: unknown) => ReactNode);
  readonly resetKeys?: ReadonlyArray<unknown>;
}

interface RenderErrorBoundaryState {
  readonly failed: boolean;
  readonly error?: unknown;
  readonly resetKeys?: ReadonlyArray<unknown> | undefined;
}

export class RenderErrorBoundary extends Component<
  RenderErrorBoundaryProps,
  RenderErrorBoundaryState
> {
  override state: RenderErrorBoundaryState = { failed: false, resetKeys: this.props.resetKeys };

  // Retry changed inputs without remounting healthy children or their controls.
  static getDerivedStateFromProps(
    { resetKeys }: RenderErrorBoundaryProps,
    state: RenderErrorBoundaryState,
  ) {
    if (
      resetKeys?.length !== state.resetKeys?.length ||
      resetKeys?.some((key, index) => !Object.is(key, state.resetKeys?.[index]))
    ) {
      return { failed: false, error: undefined, resetKeys };
    }
    return null;
  }

  static getDerivedStateFromError(error: unknown) {
    return { failed: true, error };
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    const { fallback } = this.props;
    return typeof fallback === "function" ? fallback(this.state.error) : fallback;
  }
}
